import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { ArrowLeft, Copy, Trash2, Plus, CheckCircle2, AlertTriangle, PenLine } from "lucide-react";
import { MailSignatureDialog } from "./MailSignatureDialog";
import { MailDnsRecords } from "./MailDnsRecords";

export const MailSetup = ({ onClose }) => {
  const [d, setD] = useState(null);
  const [local, setLocal] = useState("");
  const [owner, setOwner] = useState("");
  const [name, setName] = useState("");
  const [sigBox, setSigBox] = useState(null);
  const load = useCallback(() => api.get("/admin/mail/setup").then((r) => setD(r.data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Skeleton className="h-96 rounded-2xl" />;

  const add = () => api.post("/admin/mail/mailboxes", { local, user_id: owner, display_name: name })
    .then((r) => { toast.success(`${r.data.address} created`); setLocal(""); setName(""); load(); }).catch((e) => toast.error(errText(e)));
  const remove = (a) => window.confirm(`Remove ${a}? Existing emails stay, but only admins will see new mail to it.`) &&
    api.delete(`/admin/mail/mailboxes/${encodeURIComponent(a)}`).then(load);
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));
  const status = d.domain?.status;
  const ok = status === "verified";

  return (
    <div className="space-y-5" data-testid="mail-setup">
      <Button variant="ghost" onClick={onClose} data-testid="mail-setup-back" className="gap-1.5"><ArrowLeft size={16} /> Back to email</Button>
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-3">
        <h3 className="font-display text-lg font-bold text-[#12234A]">1. Connect {d.domain_name}</h3>
        <p className="text-sm flex items-center gap-2" data-testid="mail-domain-status">
          {ok ? <CheckCircle2 size={16} className="text-emerald-600" /> : <AlertTriangle size={16} className="text-amber-600" />}
          {!d.has_key ? "No Resend key saved yet." : status === "error" ? `Resend says: ${d.domain.error}` :
            status === "missing" ? `${d.domain_name} isn't added in your Resend account yet (Domains → Add domain).` :
              ok ? "Domain verified — sending is ready." : `Domain status: ${status} — add the DNS records below at your domain registrar.`}
        </p>
        {d.domain?.records && <MailDnsRecords records={d.domain.records} onCopy={copy} />}
        <div className="text-sm text-slate-600 space-y-1.5">
          <p><b>Receiving:</b> in Resend, turn on Receiving for {d.domain_name}, and add the MX record it shows you at your domain registrar.</p>
          <p><b>Webhook:</b> in Resend → Webhooks → Add webhook, choose the <code>email.received</code> event and use this URL, then save its signing secret in the app's keys.</p>
          <div className="flex items-center gap-2"><code className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs flex-1 truncate" data-testid="mail-webhook-url">{d.webhook_url}</code>
            <Button size="sm" variant="outline" onClick={() => copy(d.webhook_url)} data-testid="mail-webhook-copy"><Copy size={14} /></Button></div>
          <p className="text-xs text-slate-400">Use your live site address (critterposts.ca) for the webhook once the site is published.</p>
        </div>
      </section>
      <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-lg font-bold text-[#12234A]">2. Staff addresses</h3>
        <p className="text-sm text-slate-500">Each staff member only sees their own addresses. Admins see everything, including mail sent to addresses nobody owns.</p>
        <div className="grid sm:grid-cols-[1.2fr_1fr_1fr_auto] gap-2 items-center">
          <div className="flex items-center"><Input value={local} onChange={(e) => setLocal(e.target.value)} placeholder="support" className="rounded-r-none" data-testid="mail-new-local" />
            <span className="text-sm text-slate-500 border border-l-0 border-slate-200 rounded-r-md h-9 px-2 flex items-center bg-slate-50">@{d.domain_name}</span></div>
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger data-testid="mail-new-owner"><SelectValue placeholder="Staff member" /></SelectTrigger>
            <SelectContent>{d.staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.username || s.name} ({s.role})</SelectItem>)}</SelectContent>
          </Select>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sender name (e.g. CritterPosts Support)" data-testid="mail-new-name" />
          <Button onClick={add} disabled={!local.trim() || !owner} data-testid="mail-new-add" className="bg-[#F5551E] hover:bg-[#e04a15] gap-1"><Plus size={15} /> Add</Button>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="mail-mailbox-list">
          {d.mailboxes.map((b) => (
            <li key={b.address} className="flex items-center justify-between py-2.5 text-sm">
              <span><b className="text-[#12234A]">{b.address}</b> <span className="text-slate-400">· {b.owner_name} · sends as “{b.display_name}”</span></span>
              <span className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={() => setSigBox(b)} data-testid={`mail-sig-${b.address}`} className="gap-1.5"><PenLine size={14} /> Signature</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(b.address)} data-testid={`mail-remove-${b.address}`} className="text-red-600"><Trash2 size={15} /></Button>
              </span>
            </li>
          ))}
          {!d.mailboxes.length && <li className="py-3 text-sm text-slate-400">No addresses yet.</li>}
        </ul>
      </section>
      {sigBox && <MailSignatureDialog box={sigBox} onClose={() => setSigBox(null)} onSaved={() => { setSigBox(null); load(); }} />}
    </div>
  );
};
